import logger from '@/utils/logger';
/**
 * 上传服务 - 处理反馈数据的上传逻辑
 * 提供超时控制、批量上传、结果统计等功能
 */

export interface UploadConfig {
  timeout: number;
  maxRetries?: number;
  retryDelay?: number;
}

export interface UploadResult {
  success: boolean;
  id: string;
  reason?: string;
}

export interface BatchUploadResult {
  successful: number;
  failed: number;
  results: UploadResult[];
}

export interface BackupItem {
  id: string;
  data: any;
  token: string;
  timestamp: number;
  sceneId: string;
}

export class UploadService {
  private config: UploadConfig;

  constructor(config: UploadConfig = { timeout: 15000 }) {
    this.config = config;
  }

  /**
   * 带超时的上传操作
   */
  async uploadWithTimeout<T>(
    uploadFn: () => Promise<T>,
    timeout: number = this.config.timeout
  ): Promise<T> {
    const uploadTimeout = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('上传超时')), timeout);
    });

    return Promise.race([uploadFn(), uploadTimeout]);
  }

  /**
   * 验证上传结果
   */
  private validateUploadResult(result: any): boolean {
    return result !== false && result !== null && result !== undefined;
  }

  /**
   * 检查数据是否新鲜（10分钟内）
   */
  private isDataFresh(timestamp: number, maxAge: number = 600000): boolean {
    return Date.now() - timestamp < maxAge;
  }

  /**
   * 单个数据上传
   */
  async uploadSingle(
    backup: BackupItem,
    uploadFn: (data: any, token: string) => Promise<any>
  ): Promise<UploadResult> {
    try {
      // 检查数据新鲜度
      if (!this.isDataFresh(backup.timestamp)) {
        logger.info(`[UploadService] 数据已过期，跳过上传:`, backup.id);
        return { success: false, id: backup.id, reason: 'expired' };
      }

      // 执行上传
      const result = await this.uploadWithTimeout(() => uploadFn(backup.data, backup.token));

      // 验证结果
      if (!this.validateUploadResult(result)) {
        throw new Error('上传失败');
      }

      logger.info(`[UploadService] 反馈数据上传成功:`, backup.id);
      return { success: true, id: backup.id };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.warn(`[UploadService] 反馈数据上传失败:`, backup.id, errorMessage);
      return { success: false, id: backup.id, reason: errorMessage };
    }
  }

  /**
   * 批量上传
   */
  async batchUpload(
    backupList: BackupItem[],
    uploadFn: (data: any, token: string) => Promise<any>
  ): Promise<BatchUploadResult> {
    logger.info(
      `[UploadService] 开始批量上传 ${backupList.length} 条数据`,
      backupList.map((b) => ({
        id: b.id,
        timestamp: new Date(b.timestamp).toLocaleString(),
        messageCount: b.data?.conversations?.length || 0,
      }))
    );

    // 创建上传任务
    const uploadPromises = backupList.map((backup) => this.uploadSingle(backup, uploadFn));

    // 等待所有上传完成
    const results = await Promise.allSettled(uploadPromises);

    // 统计结果
    let successful = 0;
    let failed = 0;
    const uploadResults: UploadResult[] = [];

    results.forEach((result, index) => {
      const backup = backupList[index];
      if (result.status === 'fulfilled') {
        if (result.value.success) {
          successful++;
          logger.info(`[UploadService] 上传成功: ${backup.id}`);
        } else {
          failed++;
          logger.warn(`[UploadService] 上传失败: ${backup.id}, 原因: ${result.value.reason}`);
        }
        uploadResults.push(result.value);
      } else {
        // Promise 被拒绝的情况
        failed++;
        const errorResult: UploadResult = {
          success: false,
          id: backup.id,
          reason: result.reason instanceof Error ? result.reason.message : String(result.reason),
        };
        uploadResults.push(errorResult);
        logger.warn(`[UploadService] Promise 被拒绝: ${backup.id}`, result.reason);
      }
    });

    logger.info(`[UploadService] 批量上传完成: 成功 ${successful} 条, 失败 ${failed} 条`);

    return {
      successful,
      failed,
      results: uploadResults,
    };
  }

  /**
   * 更新配置
   */
  updateConfig(config: Partial<UploadConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// 默认上传服务实例
export const uploadService = new UploadService();
